import { useEffect, useRef } from "react";
import { LngLatBounds, Map as MLMap, NavigationControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { buildOsmStyle, MAP_CONTAINER_BACKGROUND } from "../lib/mapStyle";
import { BRIDGE_DASH, BRIDGE_OPACITY_SCALE, chainEdges } from "../lib/pathFx";
import { mapHops, type Drawable } from "../lib/pathInspector";
import { navigate } from "../lib/router";

// One route drawn on the same MapLibre base map as the Live Map. A hop that resolved to a node with a position is a dot;
// consecutive drawn hops are joined by a solid line, and when unclear, unknown or unplaced hops lie between two drawn
// ones the line is dim and dashed (the Live Map's bridging, lib/pathFx.ts). Nothing is placed for a hop that is not one
// node. Rebuilt for each route and theme (the parent keys it).

const EMPTY = { type: "FeatureCollection", features: [] };

function cssVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

export function PathInspectorMap({ route, theme, height }: { route: Drawable; theme: "light" | "dark"; height: number }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const hops = mapHops(route);
    const edges = chainEdges(hops);
    const placed = hops.filter((h) => h.pos !== null);
    const lines = {
      type: "FeatureCollection",
      features: edges.map((e) => ({
        type: "Feature",
        properties: { bridged: e.bridged },
        geometry: { type: "LineString", coordinates: [[e.from.lon, e.from.lat], [e.to.lon, e.to.lat]] },
      })),
    };
    const dots = {
      type: "FeatureCollection",
      features: placed.map((h, k) => ({
        type: "Feature",
        properties: { node: route.hops[h.index].node_id, end: k === 0 || k === placed.length - 1 },
        geometry: { type: "Point", coordinates: [h.pos!.lon, h.pos!.lat] },
      })),
    };
    const map = new MLMap({
      container: el,
      style: buildOsmStyle(theme),
      center: [-114, 44],
      zoom: 5,
      attributionControl: { compact: true },
      canvasContextAttributes: { preserveDrawingBuffer: true },
    });
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    const init = () => {
      const ink = cssVar(el, "--ink", "#111");
      const surface = cssVar(el, "--surface", "#fff");
      map.addSource("pi-lines", { type: "geojson", data: (lines ?? EMPTY) as never });
      map.addSource("pi-dots", { type: "geojson", data: (dots ?? EMPTY) as never });
      map.addLayer({
        id: "pi-solid", type: "line", source: "pi-lines", filter: ["==", ["get", "bridged"], false],
        layout: { "line-cap": "round" }, paint: { "line-color": ink, "line-width": 3, "line-opacity": 0.85 },
      });
      map.addLayer({
        id: "pi-bridge", type: "line", source: "pi-lines", filter: ["==", ["get", "bridged"], true],
        paint: { "line-color": ink, "line-width": 2.5, "line-opacity": 0.85 * BRIDGE_OPACITY_SCALE * 2, "line-dasharray": BRIDGE_DASH },
      });
      map.addLayer({
        id: "pi-dots", type: "circle", source: "pi-dots",
        paint: {
          "circle-color": ["case", ["get", "end"], ink, surface],
          "circle-radius": ["case", ["get", "end"], 7, 5],
          "circle-stroke-width": 2.5,
          "circle-stroke-color": ink,
        },
      });
      if (placed.length === 1) {
        map.jumpTo({ center: [placed[0].pos!.lon, placed[0].pos!.lat], zoom: 9 });
      } else if (placed.length > 1) {
        const b = new LngLatBounds();
        for (const h of placed) b.extend([h.pos!.lon, h.pos!.lat]);
        map.fitBounds(b, { padding: 40, maxZoom: 10, duration: 0 });
      }
    };
    if (map.isStyleLoaded()) init();
    else map.once("style.load", init);
    map.on("mouseenter", "pi-dots", () => (map.getCanvas().style.cursor = "pointer"));
    map.on("mouseleave", "pi-dots", () => (map.getCanvas().style.cursor = ""));
    map.on("click", "pi-dots", (e) => {
      const id = e.features?.[0]?.properties?.node;
      if (id) navigate(`#/nodes/${encodeURIComponent(String(id))}`);
    });
    return () => map.remove();
  }, [route, theme]);

  return (
    <div className="pi-map" style={{ height, background: MAP_CONTAINER_BACKGROUND[theme] }}>
      <div ref={ref} className="pi-map-canvas" />
    </div>
  );
}
